import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button } from '@mui/material'
import {
  ElectricBoltOutlined,
  GroupOutlined,
  PersonOutlineOutlined,
  QueryBuilderOutlined,
} from '@mui/icons-material'
import { Drawer, FilterOption } from '../../components'
import {
  translate,
  translateEventType,
} from '../../utils/filterDrawer/traslationByKeys'
import { useTranslation } from "react-i18next"

const FilterContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`

const FilterHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`

const CustomFilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0 0 0;

  > span {
    color: #858b92;
  }
`

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 8px;
  margin: 8px 0 0 0;
`

const CustomHR = styled.hr`
  border: 1px solid #d6d7db;
  margin: 16px 0;
`

const initialState = []

const FilterDrawer = ({
  open,
  onClose,
  loading,
  onSave,
  options,
  withFatigueLevels,
  withCounterMeasure,
  withSeverity,
  withShift,
  withPosition,
  withTeam,
  withNotificationStatus,
  withEventType,
}) => {
  const [selectedFatigueLevels, setFatigueLevels] = useState(initialState)
  const [selectedShifts, setSelectedShifts] = useState(initialState)
  const [selectedPositions, setSelectedPositions] = useState(initialState)
  const [selectedLevels, setSelectedLevels] = useState(initialState)
  const [selectedTeams, setSelectedTeams] = useState(initialState)
  const [selectedNotificationsStatuses, setSelectedNotificationsStatuses] =
    useState(initialState)
  const [selectedCounterMeasureStatuses, setSelectedCounterMeasureStatuses] =
    useState(initialState)
  const [selectedSeverities, setSelectedSeverities] = useState(initialState)
  const [selectedEventTypes, setSelectedEventTypes] = useState(initialState)
  const { t } = useTranslation('common')

  const WithoutOptions = () => {
    return (
      <span style={{ fontSize: 14, color: '#858b92' }}>
        {t('filters.none')}
      </span>
    )
  }

  const handleSelection = ({ value, getState, setState }) => {
    const haveValue = getState.includes(value)

    if (!haveValue) {
      setState(prev => [...prev, value])
      return
    }

    const removedValue = getState.filter(item => item !== value)

    setState(removedValue)
    return
  }

  const isSelected = ({ getState, value }) => {
    return getState.includes(value)
  }

  const handleOnSave = () => {
    const filters = {
      fatigueLevels: selectedFatigueLevels,
      shifts: selectedShifts,
      positions: selectedPositions,
      levels: selectedLevels,
      teams: selectedTeams,
      notificationsStatuses: selectedNotificationsStatuses,
      counterMeasureStatuses: selectedCounterMeasureStatuses,
      severities: selectedSeverities,
      eventTypes: selectedEventTypes,
    }

    onSave(filters)
    onClose()
  }

  const onReset = () => {
    setFatigueLevels(initialState)
    setSelectedShifts(initialState)
    setSelectedPositions(initialState)
    setSelectedLevels(initialState)
    setSelectedTeams(initialState)
    setSelectedNotificationsStatuses(initialState)
    setSelectedCounterMeasureStatuses(initialState)
    setSelectedSeverities(initialState)
    setSelectedEventTypes(initialState)

    const filters = {
      fatigueLevels: initialState,
      shifts: initialState,
      positions: initialState,
      levels: initialState,
      teams: initialState,
      notificationsStatuses: initialState,
      counterMeasureStatuses: initialState,
      severities: initialState,
      eventTypes: initialState,
    }

    onSave(filters)
    onClose()
  }

  useEffect(() => {
    if (open && localStorage.getItem('@ialerta/reset-filters')) {
      setFatigueLevels(initialState)
      setSelectedShifts(initialState)
      setSelectedPositions(initialState)
      setSelectedLevels(initialState)
      setSelectedTeams(initialState)
      setSelectedNotificationsStatuses(initialState)
      setSelectedCounterMeasureStatuses(initialState)
      setSelectedSeverities(initialState)
      setSelectedEventTypes(initialState)

      localStorage.removeItem('@ialerta/reset-filters')
    }
  }, [open])

  return (
    <Drawer
      title={t('filters.title')}
      open={open}
      onClose={onClose}
    >
      <Drawer.Body loading={loading}>
        <FilterContainer>
          {withFatigueLevels && (
            <div>
              <FilterHeader>
                <ElectricBoltOutlined />
                <span>{t('filter.alert_level')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.fatigueLevels?.length > 0 ? (
                  <>
                    {options?.fatigueLevels?.map(fatigueLevel => (
                      <FilterOption
                        key={fatigueLevel}
                        name={translate(fatigueLevel)}
                        onClick={() =>
                          handleSelection({
                            value: fatigueLevel,
                            getState: selectedFatigueLevels,
                            setState: setFatigueLevels,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedFatigueLevels,
                          value: fatigueLevel,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
              <CustomHR />
            </div>
          )}
          {withShift && (
            <div>
              <FilterHeader>
                <QueryBuilderOutlined />
                <span>{t('filter.shift')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.shifts?.length > 0 ? (
                  <>
                    {options?.shifts?.map(shift => (
                      <FilterOption
                        key={shift}
                        name={shift}
                        onClick={() =>
                          handleSelection({
                            value: shift,
                            getState: selectedShifts,
                            setState: setSelectedShifts,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedShifts,
                          value: shift,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
              <CustomHR />
            </div>
          )}
          {withPosition && (
            <div>
              <FilterHeader>
                <PersonOutlineOutlined />
                <span>{t('filter.role')}</span>
              </FilterHeader>
              <CustomFilterOptions>
                <span>{t('filter.level')}</span>
                <FilterOptions>
                  {options?.levels?.length > 0 ? (
                    <>
                      {options?.levels?.map(level => (
                        <FilterOption
                          key={level}
                          name={level}
                          onClick={() =>
                            handleSelection({
                              value: level,
                              getState: selectedLevels,
                              setState: setSelectedLevels,
                            })
                          }
                          isSelected={isSelected({
                            getState: selectedLevels,
                            value: level,
                          })}
                          dismissable
                        />
                      ))}
                    </>
                  ) : (
                    <WithoutOptions />
                  )}
                </FilterOptions>
              </CustomFilterOptions>
              <CustomFilterOptions>
                <span>{t('filter.position')}</span>
                <FilterOptions>
                  {options?.positions?.length > 0 ? (
                    <>
                      {options?.positions?.map(position => (
                        <FilterOption
                          key={position}
                          name={position}
                          onClick={() =>
                            handleSelection({
                              value: position,
                              getState: selectedPositions,
                              setState: setSelectedPositions,
                            })
                          }
                          isSelected={isSelected({
                            getState: selectedPositions,
                            value: position,
                          })}
                          dismissable
                        />
                      ))}
                    </>
                  ) : (
                    <WithoutOptions />
                  )}
                </FilterOptions>
              </CustomFilterOptions>
              <CustomHR />
            </div>
          )}
          {withTeam && (
            <div>
              <FilterHeader>
                <GroupOutlined />
                <span>{t('filter.team')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.teams?.length > 0 ? (
                  <>
                    {options?.teams?.map(team => (
                      <FilterOption
                        key={team}
                        name={team}
                        onClick={() =>
                          handleSelection({
                            value: team,
                            getState: selectedTeams,
                            setState: setSelectedTeams,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedTeams,
                          value: team,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
              <CustomHR />
            </div>
          )}
          {withCounterMeasure && (
            <div>
              <FilterHeader>
                <GroupOutlined />
                <span>{t('filter.status')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.counterMeasureStatuses?.length > 0 ? (
                  <>
                    {options?.counterMeasureStatuses?.map(
                      counterMeasureStatus => (
                        <FilterOption
                          key={counterMeasureStatus}
                          name={translate(counterMeasureStatus)}
                          onClick={() =>
                            handleSelection({
                              value: counterMeasureStatus,
                              getState: selectedCounterMeasureStatuses,
                              setState: setSelectedCounterMeasureStatuses,
                            })
                          }
                          isSelected={isSelected({
                            getState: selectedCounterMeasureStatuses,
                            value: counterMeasureStatus,
                          })}
                          dismissable
                        />
                      ),
                    )}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
              <CustomHR />
            </div>
          )}
          {withSeverity && (
            <div>
              <FilterHeader>
                <GroupOutlined />
                <span>{t('filter.severity')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.severities?.length > 0 ? (
                  <>
                    {options?.severities?.map(severity => (
                      <FilterOption
                        key={severity}
                        name={translate(severity)}
                        onClick={() =>
                          handleSelection({
                            value: severity,
                            getState: selectedSeverities,
                            setState: setSelectedSeverities,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedSeverities,
                          value: severity,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
              <CustomHR />
            </div>
          )}
          {withEventType && (
            <div>
              <FilterHeader>
                <GroupOutlined />
                <span>{t('filter.event_type')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.eventTypes?.length > 0 ? (
                  <>
                    {options?.eventTypes?.map(eventType => (
                      <FilterOption
                        key={eventType}
                        name={translateEventType(eventType)}
                        onClick={() =>
                          handleSelection({
                            value: eventType,
                            getState: selectedEventTypes,
                            setState: setSelectedEventTypes,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedEventTypes,
                          value: eventType,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
            </div>
          )}
          {withNotificationStatus && (
            <div>
              <FilterHeader>
                <GroupOutlined />
                <span>{t('filter.notification_status')}</span>
              </FilterHeader>
              <FilterOptions>
                {options?.notificationsStatuses?.length > 0 ? (
                  <>
                    {options?.notificationsStatuses?.map(notificationStatus => (
                      <FilterOption
                        key={notificationStatus}
                        name={translate(notificationStatus)}
                        onClick={() =>
                          handleSelection({
                            value: notificationStatus,
                            getState: selectedNotificationsStatuses,
                            setState: setSelectedNotificationsStatuses,
                          })
                        }
                        isSelected={isSelected({
                          getState: selectedNotificationsStatuses,
                          value: notificationStatus,
                        })}
                        dismissable
                      />
                    ))}
                  </>
                ) : (
                  <WithoutOptions />
                )}
              </FilterOptions>
            </div>
          )}
        </FilterContainer>
      </Drawer.Body>
      <Drawer.Actions>
        <Button onClick={onReset}>{t('restore')}</Button>
        <Button
          onClick={handleOnSave}
          variant='contained'
          disabled={loading}
        >
          {t('apply')}
        </Button>
      </Drawer.Actions>
    </Drawer>
  )
}

export default FilterDrawer
